import { useState, useEffect } from "react";
import CrmLayout from "@/components/feature/CrmLayout";
import { useToast } from "@/components/base/Toast";
import { fetchApi } from "@/services/apiService";

export interface ScoringFactor {
  id: string;
  factor: string;
  criteria: string;
  maxPoints: number;
  active: boolean;
}

export interface ScoringThreshold {
  id: string;
  label: string;
  color: "green" | "amber" | "red";
  icon: string;
  description: string;
  from: number;
  to: number;
}

export interface AutoAction {
  id: number;
  trigger: string;
  description: string[];
  enabled: boolean;
}

const defaultFactors: ScoringFactor[] = [
  {
    id: "source",
    factor: "Source Quality",
    criteria: "IndiaMart=20, JustDial=18, TradeIndia=16, Website=15, Social Media=14, WhatsApp=12, Manual=10",
    maxPoints: 20,
    active: true,
  },
  {
    id: "stage",
    factor: "Pipeline Stage",
    criteria: "New=5, Site Visit=8, Done=12, Proposal=15, Negotiation=18, Booking=20",
    maxPoints: 20,
    active: true,
  },
  {
    id: "priority",
    factor: "Lead Priority",
    criteria: "High=20, Medium=14, Low=8",
    maxPoints: 20,
    active: true,
  },
  {
    id: "value",
    factor: "Deal Value",
    criteria: ">1Cr=20, 50L-1Cr=16, 25L-50L=12, 10L-25L=8, <10L=5",
    maxPoints: 20,
    active: true,
  },
  {
    id: "profile",
    factor: "Profile Completeness",
    criteria: "Phone=5, Email=5, Company=5, Industry/Project=5",
    maxPoints: 20,
    active: true,
  },
  {
    id: "engagement",
    factor: "Engagement Level",
    criteria: "WA reply=+5, Email open=+3, Callback=+5, No engage=-5",
    maxPoints: 10,
    active: false,
  },
];

const defaultThresholds: ScoringThreshold[] = [
  {
    id: "hot",
    label: "Hot Lead",
    color: "green",
    icon: "ri-fire-fill",
    description: "Hot leads get priority assignment",
    from: 71,
    to: 100,
  },
  {
    id: "warm",
    label: "Warm Lead",
    color: "amber",
    icon: "ri-star-fill",
    description: "Warm leads assigned by round robin",
    from: 41,
    to: 70,
  },
  {
    id: "cold",
    label: "Cold Lead",
    color: "red",
    icon: "ri-snowy-fill",
    description: "Cold leads go to unassigned queue",
    from: 0,
    to: 40,
  },
];

const thresholdColors: Record<string, { card: string; border: string; icon: string; text: string; desc: string }> = {
  green: {
    card: "bg-green-50",
    border: "border-green-200",
    icon: "text-green-600",
    text: "text-green-800",
    desc: "text-green-700",
  },
  amber: {
    card: "bg-amber-50",
    border: "border-amber-200",
    icon: "text-amber-600",
    text: "text-amber-800",
    desc: "text-amber-700",
  },
  red: {
    card: "bg-red-50",
    border: "border-red-200",
    icon: "text-red-600",
    text: "text-red-800",
    desc: "text-red-700",
  },
};

export default function ScoringConfigPage() {
  const { showToast } = useToast();
  const [scoringMethod, setScoringMethod] = useState<"automatic" | "manual">("automatic");
  const [factors, setFactors] = useState<ScoringFactor[]>(defaultFactors);
  const [thresholds, setThresholds] = useState<ScoringThreshold[]>(defaultThresholds);
  const [actions, setActions] = useState<AutoAction[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [isRecalculating, setIsRecalculating] = useState(false);

  // Live dynamic CRM context
  const [liveAgents, setLiveAgents] = useState<string[]>([]);
  const [liveStats, setLiveStats] = useState<{ total: number; hot: number; warm: number; cold: number; avgScore: number }>({
    total: 0,
    hot: 0,
    warm: 0,
    cold: 0,
    avgScore: 50,
  });

  // Modals state
  const [editingFactor, setEditingFactor] = useState<ScoringFactor | null>(null);
  const [showAddFactorModal, setShowAddFactorModal] = useState(false);
  const [newFactorForm, setNewFactorForm] = useState({ factor: "", criteria: "", maxPoints: 20 });

  const [editingAction, setEditingAction] = useState<AutoAction | null>(null);
  const [showAddActionModal, setShowAddActionModal] = useState(false);
  const [newActionForm, setNewActionForm] = useState({ trigger: "", description: "", targetAgent: "" });

  // Load from backend
  useEffect(() => {
    // 1. Load scoring configuration
    fetchApi<any>("/config/scoring", undefined, null)
      .then((res) => {
        const data = res?.data || res;
        if (data) {
          if (data.method) setScoringMethod(data.method);
          if (Array.isArray(data.factors) && data.factors.length > 0) setFactors(data.factors);
          if (Array.isArray(data.thresholds) && data.thresholds.length > 0) setThresholds(data.thresholds);
          if (Array.isArray(data.actions) && data.actions.length > 0) {
            setActions(data.actions);
          } else {
            setActions([
              {
                id: 1,
                trigger: "When lead becomes Hot (>70)",
                description: ["Auto-assign to primary agent", "Send WhatsApp welcome message"],
                enabled: true,
              },
              {
                id: 2,
                trigger: "When lead drops to Cold (<40)",
                description: ["Add to re-engagement sequence", "Notify assigned agent"],
                enabled: true,
              },
              {
                id: 3,
                trigger: "When score unchanged for 7 days",
                description: ["Flag for manual review", "Send activity reminder to agent"],
                enabled: false,
              },
            ]);
          }
        }
      })
      .catch(() => {});

    // 2. Load live dynamic team agents
    fetchApi<any>("/auth/company-user/all", undefined, null)
      .then((res) => {
        const userList = Array.isArray(res)
          ? res
          : Array.isArray(res?.items)
          ? res.items
          : Array.isArray(res?.data?.items)
          ? res.data.items
          : Array.isArray(res?.data)
          ? res.data
          : [];

        const names: string[] = [];
        userList.forEach((u: any) => {
          const name = u.name || u.fullName || u.username;
          if (name && !names.includes(name)) names.push(name);
        });
        if (names.length > 0) setLiveAgents(names);
      })
      .catch(() => {});

    // 3. Load live lead scoring statistics
    fetchApi<any>("/leads/stats", undefined, null)
      .then((res) => {
        const data = res?.data || res;
        if (data) {
          setLiveStats({
            total: Number(data.total) || 0,
            hot: Number(data.hot) || 0,
            warm: Number(data.warm) || 0,
            cold: Number(data.cold) || 0,
            avgScore: Number(data.avgScore) || 50,
          });
        }
      })
      .catch(() => {});
  }, []);

  const handleFactorToggle = (id: string) => {
    setFactors((prev) =>
      prev.map((f) => (f.id === id ? { ...f, active: !f.active } : f))
    );
  };

  const handleActionToggle = (id: number) => {
    setActions((prev) =>
      prev.map((a) => (a.id === id ? { ...a, enabled: !a.enabled } : a))
    );
  };

  const handleThresholdChange = (id: string, field: "from" | "to", value: number) => {
    setThresholds((prev) =>
      prev.map((t) => (t.id === id ? { ...t, [field]: value } : t))
    );
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await fetchApi("/config/scoring", {
        method: "POST",
        body: JSON.stringify({
          method: scoringMethod,
          factors,
          thresholds,
          actions,
        }),
      });
      showToast("Lead scoring configuration saved successfully", "success");

      // Refresh live stats after save
      fetchApi<any>("/leads/stats", undefined, null)
        .then((res) => {
          const data = res?.data || res;
          if (data) {
            setLiveStats({
              total: Number(data.total) || 0,
              hot: Number(data.hot) || 0,
              warm: Number(data.warm) || 0,
              cold: Number(data.cold) || 0,
              avgScore: Number(data.avgScore) || 50,
            });
          }
        })
        .catch(() => {});
    } catch (err: any) {
      showToast(err.message || "Failed to save scoring configuration", "error");
    } finally {
      setIsSaving(false);
    }
  };

  const handleRecalculateScores = async () => {
    setIsRecalculating(true);
    try {
      await fetchApi("/config/scoring", {
        method: "POST",
        body: JSON.stringify({
          method: scoringMethod,
          factors,
          thresholds,
          actions,
        }),
      });

      const res = await fetchApi<any>("/config/scoring/recalculate", { method: "POST" });
      const count = res?.count || 0;
      showToast(`Successfully recalculated scores for ${count} leads!`, "success");

      // Refresh stats
      fetchApi<any>("/leads/stats", undefined, null)
        .then((res) => {
          const data = res?.data || res;
          if (data) {
            setLiveStats({
              total: Number(data.total) || 0,
              hot: Number(data.hot) || 0,
              warm: Number(data.warm) || 0,
              cold: Number(data.cold) || 0,
              avgScore: Number(data.avgScore) || 50,
            });
          }
        })
        .catch(() => {});
    } catch (err: any) {
      showToast(err.message || "Failed to recalculate lead scores", "error");
    } finally {
      setIsRecalculating(false);
    }
  };

  // Edit Factor Handlers
  const handleSaveEditedFactor = (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingFactor) return;
    setFactors((prev) =>
      prev.map((f) => (f.id === editingFactor.id ? { ...editingFactor } : f))
    );
    setEditingFactor(null);
    showToast("Factor updated", "success");
  };

  const handleAddNewFactor = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newFactorForm.factor.trim()) return;
    const newFactor: ScoringFactor = {
      id: newFactorForm.factor.toLowerCase().replace(/[^a-z0-9]/g, "_") + "_" + Date.now(),
      factor: newFactorForm.factor.trim(),
      criteria: newFactorForm.criteria.trim() || "Dynamic scoring criteria",
      maxPoints: Number(newFactorForm.maxPoints) || 20,
      active: true,
    };
    setFactors((prev) => [...prev, newFactor]);
    setShowAddFactorModal(false);
    setNewFactorForm({ factor: "", criteria: "", maxPoints: 20 });
    showToast("New scoring factor added", "success");
  };

  const handleDeleteFactor = (id: string) => {
    setFactors((prev) => prev.filter((f) => f.id !== id));
    setEditingFactor(null);
    showToast("Factor removed", "info");
  };

  // Action Handlers
  const handleSaveEditedAction = (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingAction) return;
    setActions((prev) =>
      prev.map((a) => (a.id === editingAction.id ? { ...editingAction } : a))
    );
    setEditingAction(null);
    showToast("Automated action updated", "success");
  };

  const handleAddNewAction = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newActionForm.trigger.trim()) return;
    const descArray = newActionForm.description
      .split("\n")
      .map((s) => s.trim())
      .filter(Boolean);

    const newAct: AutoAction = {
      id: Date.now(),
      trigger: newActionForm.trigger.trim(),
      description: descArray.length > 0 ? descArray : ["Notify assigned agent"],
      enabled: true,
    };
    setActions((prev) => [...prev, newAct]);
    setShowAddActionModal(false);
    setNewActionForm({ trigger: "", description: "", targetAgent: "" });
    showToast("New automated action added", "success");
  };

  const handleDeleteAction = (id: number) => {
    setActions((prev) => prev.filter((a) => a.id !== id));
    setEditingAction(null);
    showToast("Action removed", "info");
  };

  return (
    <CrmLayout title="Lead Scoring">
      <div className="p-5 md:p-6 space-y-5 max-w-[1300px] mx-auto">
        {/* Page Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 bg-white p-5 rounded-2xl border border-gray-200 shadow-sm">
          <div>
            <h1 className="text-xl font-bold text-gray-900">Lead Scoring Rules</h1>
            <p className="text-sm text-gray-500 mt-0.5">
              Configure how leads are scored automatically across your CRM
            </p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <button
              onClick={handleRecalculateScores}
              disabled={isRecalculating || isSaving}
              title="Recalculate scores for all leads in the database"
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-indigo-200 bg-indigo-50 hover:bg-indigo-100 disabled:opacity-50 text-indigo-700 text-sm font-semibold transition-all whitespace-nowrap cursor-pointer shadow-xs"
            >
              {isRecalculating ? <i className="ri-loader-4-line animate-spin" /> : <i className="ri-refresh-line" />}
              <span>{isRecalculating ? "Recalculating..." : "Recalculate Scores"}</span>
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving || isRecalculating}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white text-sm font-semibold transition-all duration-200 active:scale-[0.98] whitespace-nowrap cursor-pointer shadow-sm"
            >
              {isSaving ? <i className="ri-loader-4-line animate-spin" /> : <i className="ri-save-line" />}
              {isSaving ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </div>

        {/* SECTION 1: Scoring Method */}
        <div className="bg-white rounded-2xl border border-gray-200 p-5 shadow-sm">
          <h2 className="text-base font-semibold text-gray-900 mb-1">Scoring Method</h2>
          <p className="text-sm text-gray-500 mb-5">
            Choose how lead scores are determined
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Automatic */}
            <button
              type="button"
              onClick={() => setScoringMethod("automatic")}
              className={`flex items-start gap-4 p-4 rounded-xl text-left transition-all duration-200 cursor-pointer ${
                scoringMethod === "automatic"
                  ? "border-2 border-indigo-600 bg-indigo-50/70 shadow-sm"
                  : "border border-gray-200 bg-white hover:border-indigo-300"
              }`}
            >
              <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center flex-shrink-0">
                <i className="ri-flashlight-fill text-lg text-indigo-600 w-5 h-5 flex items-center justify-center" />
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-900">Automatic Scoring</p>
                <p className="text-xs text-gray-500 mt-0.5">
                  Score calculated dynamically from active rules & factors
                </p>
              </div>
            </button>

            {/* Manual */}
            <button
              type="button"
              onClick={() => setScoringMethod("manual")}
              className={`flex items-start gap-4 p-4 rounded-xl text-left transition-all duration-200 cursor-pointer ${
                scoringMethod === "manual"
                  ? "border-2 border-indigo-600 bg-indigo-50/70 shadow-sm"
                  : "border border-gray-200 bg-white hover:border-indigo-300"
              }`}
            >
              <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                <i className="ri-pencil-line text-lg text-gray-600 w-5 h-5 flex items-center justify-center" />
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-900">Manual Scoring</p>
                <p className="text-xs text-gray-500 mt-0.5">
                  Agents set and update score values manually
                </p>
              </div>
            </button>
          </div>
        </div>

        {/* SECTION 2: Scoring Factors */}
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="p-5 border-b border-gray-100 flex items-center justify-between">
            <div>
              <h2 className="text-base font-semibold text-gray-900">Scoring Factors</h2>
              <p className="text-sm text-gray-500 mt-0.5">
                Each active factor contributes to total lead score (max 100)
              </p>
            </div>
            <button
              onClick={() => setShowAddFactorModal(true)}
              className="px-3 py-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 text-xs font-semibold text-gray-700 transition-colors flex items-center gap-1 cursor-pointer"
            >
              <i className="ri-add-line" />
              <span>Add Factor</span>
            </button>
          </div>

          {/* Table Header */}
          <div className="grid grid-cols-12 gap-4 px-5 py-3 bg-gray-50 border-b border-gray-200 text-xs font-semibold uppercase text-gray-500 tracking-wider">
            <div className="col-span-3">Factor</div>
            <div className="col-span-4">Criteria</div>
            <div className="col-span-2 text-center">Max Points</div>
            <div className="col-span-2 text-center">Active</div>
            <div className="col-span-1 text-right">Actions</div>
          </div>

          {/* Table Rows */}
          <div className="divide-y divide-gray-100">
            {factors.map((f) => (
              <div
                key={f.id}
                className="grid grid-cols-12 gap-4 px-5 py-4 items-center hover:bg-gray-50/60 transition-colors"
              >
                {/* Factor name */}
                <div className="col-span-3">
                  <p className="text-sm font-semibold text-gray-900">{f.factor}</p>
                </div>

                {/* Criteria */}
                <div className="col-span-4">
                  <p className="text-xs text-gray-500 leading-relaxed truncate" title={f.criteria}>
                    {f.criteria}
                  </p>
                </div>

                {/* Max Points */}
                <div className="col-span-2 flex justify-center">
                  <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-indigo-50 text-indigo-700 border border-indigo-100">
                    {f.maxPoints} pts
                  </span>
                </div>

                {/* Active toggle button (Pixel-Perfect UI) */}
                <div className="col-span-2 flex justify-center">
                  <button
                    type="button"
                    role="switch"
                    aria-checked={f.active}
                    onClick={() => handleFactorToggle(f.id)}
                    className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-500/20 ${
                      f.active ? "bg-indigo-600" : "bg-gray-200"
                    }`}
                  >
                    <span
                      aria-hidden="true"
                      className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                        f.active ? "translate-x-5" : "translate-x-0"
                      }`}
                    />
                  </button>
                </div>

                {/* Edit button */}
                <div className="col-span-1 flex justify-end">
                  <button
                    onClick={() => setEditingFactor(f)}
                    className="w-8 h-8 rounded-lg flex items-center justify-center text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 transition-colors cursor-pointer"
                    title="Edit factor"
                  >
                    <i className="ri-pencil-line text-base" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* SECTION 3: Score Thresholds */}
        <div className="bg-white rounded-2xl border border-gray-200 p-5 shadow-sm">
          <div className="flex items-center justify-between mb-1">
            <h2 className="text-base font-semibold text-gray-900">Score Thresholds</h2>
            <span className="text-xs text-indigo-600 font-semibold bg-indigo-50 px-2.5 py-1 rounded-full border border-indigo-100">
              Avg Lead Score: {liveStats.avgScore}/100 ({liveStats.total} Total Leads)
            </span>
          </div>
          <p className="text-sm text-gray-500 mb-5">Define score range labels and categories</p>

          <div className="space-y-3">
            {thresholds.map((t) => {
              const c = thresholdColors[t.color] || thresholdColors.green;
              const countForThreshold =
                t.id === "hot" ? liveStats.hot : t.id === "warm" ? liveStats.warm : liveStats.cold;

              return (
                <div
                  key={t.id}
                  className={`${c.card} ${c.border} border rounded-xl p-4 transition-all`}
                >
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    {/* Left: icon + label */}
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center flex-shrink-0 shadow-xs border border-gray-100">
                        <i className={`${t.icon} text-lg ${c.icon}`} />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className={`text-sm font-semibold ${c.text}`}>{t.label}</p>
                          <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-white/90 border border-gray-200/60 text-gray-700 shadow-2xs">
                            {countForThreshold} leads
                          </span>
                        </div>
                        <p className={`text-xs ${c.desc} mt-0.5`}>{t.description}</p>
                      </div>
                    </div>

                    {/* Right: score inputs */}
                    <div className="flex items-center gap-2 flex-shrink-0 self-end sm:self-auto">
                      <span className="text-xs text-gray-500 font-medium">Score</span>
                      <input
                        type="number"
                        value={t.from}
                        onChange={(e) =>
                          handleThresholdChange(t.id, "from", Number(e.target.value))
                        }
                        className="w-16 px-2.5 py-1.5 rounded-lg border border-gray-300 bg-white text-sm text-center font-semibold text-gray-800 focus:outline-none focus:ring-2 focus:ring-indigo-200 transition-all"
                        min={0}
                        max={100}
                      />
                      <span className="text-xs text-gray-400">to</span>
                      <input
                        type="number"
                        value={t.to}
                        onChange={(e) =>
                          handleThresholdChange(t.id, "to", Number(e.target.value))
                        }
                        className="w-16 px-2.5 py-1.5 rounded-lg border border-gray-300 bg-white text-sm text-center font-semibold text-gray-800 focus:outline-none focus:ring-2 focus:ring-indigo-200 transition-all"
                        min={0}
                        max={100}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* SECTION 4: Auto Actions By Score */}
        <div className="bg-white rounded-2xl border border-gray-200 p-5 shadow-sm">
          <div className="flex items-center justify-between mb-1">
            <h2 className="text-base font-semibold text-gray-900">Automated Actions</h2>
            <button
              onClick={() => setShowAddActionModal(true)}
              className="text-xs font-semibold text-indigo-600 hover:text-indigo-700 flex items-center gap-1 cursor-pointer"
            >
              <i className="ri-add-line" />
              <span>Add Action</span>
            </button>
          </div>
          <p className="text-sm text-gray-500 mb-5">
            Trigger automated CRM actions when lead score changes
          </p>

          <div className="divide-y divide-gray-100">
            {actions.map((action) => (
              <div
                key={action.id}
                className="flex items-start justify-between gap-4 py-4"
              >
                <div className="flex items-start gap-3 min-w-0">
                  {/* Status dot indicator */}
                  <span
                    className={`mt-1.5 w-2.5 h-2.5 rounded-full flex-shrink-0 ${
                      action.enabled ? "bg-green-500 ring-4 ring-green-100" : "bg-gray-300"
                    }`}
                  />
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-gray-900">
                      {action.trigger}
                    </p>
                    <div className="mt-1 space-y-0.5">
                      {action.description.map((line, i) => (
                        <p key={i} className="text-xs text-gray-500 flex items-center gap-1.5">
                          <span className="text-gray-300">•</span>
                          {line}
                        </p>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-3 flex-shrink-0">
                  {/* Active toggle button (Pixel-Perfect UI) */}
                  <button
                    type="button"
                    role="switch"
                    aria-checked={action.enabled}
                    onClick={() => handleActionToggle(action.id)}
                    className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-500/20 ${
                      action.enabled ? "bg-indigo-600" : "bg-gray-200"
                    }`}
                  >
                    <span
                      aria-hidden="true"
                      className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                        action.enabled ? "translate-x-5" : "translate-x-0"
                      }`}
                    />
                  </button>
                  <button
                    onClick={() => setEditingAction(action)}
                    className="text-xs font-semibold text-indigo-600 hover:text-indigo-700 whitespace-nowrap cursor-pointer"
                  >
                    Edit
                  </button>
                </div>
              </div>
            ))}
          </div>

          <button
            onClick={() => setShowAddActionModal(true)}
            className="w-full mt-4 px-4 py-2.5 rounded-xl border border-dashed border-indigo-300 text-sm font-semibold text-indigo-600 hover:bg-indigo-50/50 transition-colors duration-200 whitespace-nowrap cursor-pointer flex items-center justify-center gap-2"
          >
            <i className="ri-add-line text-base" />
            <span>+ Add Action</span>
          </button>
        </div>

        {/* Bottom Save */}
        <button
          onClick={handleSave}
          disabled={isSaving || isRecalculating}
          className="w-full px-6 py-3.5 rounded-2xl bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold transition-all duration-200 active:scale-[0.98] whitespace-nowrap shadow-md cursor-pointer disabled:opacity-50"
        >
          {isSaving ? "Saving Configuration..." : "Save Scoring Configuration"}
        </button>
      </div>

      {/* Edit Factor Modal */}
      {editingFactor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 animate-fade-in">
          <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between bg-gray-50/60">
              <h3 className="text-base font-bold text-gray-900">Edit Scoring Factor</h3>
              <button
                onClick={() => setEditingFactor(null)}
                className="text-gray-400 hover:text-gray-600 cursor-pointer"
              >
                <i className="ri-close-line text-lg" />
              </button>
            </div>
            <form onSubmit={handleSaveEditedFactor} className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Factor Name</label>
                <input
                  type="text"
                  required
                  value={editingFactor.factor}
                  onChange={(e) => setEditingFactor({ ...editingFactor, factor: e.target.value })}
                  className="w-full px-3.5 py-2 border border-gray-200 rounded-xl text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-500"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Criteria Description</label>
                <input
                  type="text"
                  required
                  value={editingFactor.criteria}
                  onChange={(e) => setEditingFactor({ ...editingFactor, criteria: e.target.value })}
                  className="w-full px-3.5 py-2 border border-gray-200 rounded-xl text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-500"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Max Points</label>
                <input
                  type="number"
                  required
                  min={1}
                  max={50}
                  value={editingFactor.maxPoints}
                  onChange={(e) => setEditingFactor({ ...editingFactor, maxPoints: Number(e.target.value) })}
                  className="w-full px-3.5 py-2 border border-gray-200 rounded-xl text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-500"
                />
              </div>

              <div className="flex items-center justify-between pt-2">
                <span className="text-xs font-semibold text-gray-700">Factor Active</span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={editingFactor.active}
                  onClick={() => setEditingFactor({ ...editingFactor, active: !editingFactor.active })}
                  className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-500/20 ${
                    editingFactor.active ? "bg-indigo-600" : "bg-gray-200"
                  }`}
                >
                  <span
                    aria-hidden="true"
                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                      editingFactor.active ? "translate-x-5" : "translate-x-0"
                    }`}
                  />
                </button>
              </div>

              <div className="pt-4 border-t border-gray-100 flex items-center justify-between gap-3">
                <button
                  type="button"
                  onClick={() => handleDeleteFactor(editingFactor.id)}
                  className="px-3 py-2 text-xs font-semibold text-red-600 hover:bg-red-50 rounded-lg transition-colors cursor-pointer"
                >
                  Delete
                </button>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setEditingFactor(null)}
                    className="px-4 py-2 border border-gray-200 rounded-xl text-sm text-gray-600 hover:bg-gray-50 cursor-pointer"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-sm font-semibold cursor-pointer"
                  >
                    Save
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Factor Modal */}
      {showAddFactorModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 animate-fade-in">
          <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between bg-gray-50/60">
              <h3 className="text-base font-bold text-gray-900">Add New Scoring Factor</h3>
              <button
                onClick={() => setShowAddFactorModal(false)}
                className="text-gray-400 hover:text-gray-600 cursor-pointer"
              >
                <i className="ri-close-line text-lg" />
              </button>
            </div>
            <form onSubmit={handleAddNewFactor} className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Factor Name *</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Location Proximity"
                  value={newFactorForm.factor}
                  onChange={(e) => setNewFactorForm({ ...newFactorForm, factor: e.target.value })}
                  className="w-full px-3.5 py-2 border border-gray-200 rounded-xl text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-500"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Criteria Description</label>
                <input
                  type="text"
                  placeholder="e.g. Same City=20, Same State=10"
                  value={newFactorForm.criteria}
                  onChange={(e) => setNewFactorForm({ ...newFactorForm, criteria: e.target.value })}
                  className="w-full px-3.5 py-2 border border-gray-200 rounded-xl text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-500"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Max Points</label>
                <input
                  type="number"
                  required
                  min={1}
                  max={50}
                  value={newFactorForm.maxPoints}
                  onChange={(e) => setNewFactorForm({ ...newFactorForm, maxPoints: Number(e.target.value) })}
                  className="w-full px-3.5 py-2 border border-gray-200 rounded-xl text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-500"
                />
              </div>

              <div className="pt-4 border-t border-gray-100 flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setShowAddFactorModal(false)}
                  className="px-4 py-2 border border-gray-200 rounded-xl text-sm text-gray-600 hover:bg-gray-50 cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-sm font-semibold cursor-pointer"
                >
                  Add Factor
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Action Modal */}
      {editingAction && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 animate-fade-in">
          <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between bg-gray-50/60">
              <h3 className="text-base font-bold text-gray-900">Edit Automated Action</h3>
              <button
                onClick={() => setEditingAction(null)}
                className="text-gray-400 hover:text-gray-600 cursor-pointer"
              >
                <i className="ri-close-line text-lg" />
              </button>
            </div>
            <form onSubmit={handleSaveEditedAction} className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Trigger Condition</label>
                <input
                  type="text"
                  required
                  value={editingAction.trigger}
                  onChange={(e) => setEditingAction({ ...editingAction, trigger: e.target.value })}
                  className="w-full px-3.5 py-2 border border-gray-200 rounded-xl text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-500"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Actions (One per line)
                </label>
                <textarea
                  rows={3}
                  value={editingAction.description.join("\n")}
                  onChange={(e) =>
                    setEditingAction({
                      ...editingAction,
                      description: e.target.value.split("\n"),
                    })
                  }
                  className="w-full px-3.5 py-2 border border-gray-200 rounded-xl text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-500 resize-none"
                />
              </div>

              {liveAgents.length > 0 && (
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">
                    Assign to Team Member (Optional)
                  </label>
                  <select
                    onChange={(e) => {
                      if (e.target.value) {
                        const newLines = editingAction.description.filter(
                          (l) => !l.toLowerCase().includes("auto-assign")
                        );
                        newLines.unshift(`Auto-assign to ${e.target.value}`);
                        setEditingAction({ ...editingAction, description: newLines });
                      }
                    }}
                    className="w-full px-3.5 py-2 border border-gray-200 rounded-xl text-sm text-gray-900 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-200"
                  >
                    <option value="">Select an agent...</option>
                    {liveAgents.map((ag) => (
                      <option key={ag} value={ag}>
                        {ag}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className="flex items-center justify-between pt-2">
                <span className="text-xs font-semibold text-gray-700">Action Enabled</span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={editingAction.enabled}
                  onClick={() => setEditingAction({ ...editingAction, enabled: !editingAction.enabled })}
                  className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-500/20 ${
                    editingAction.enabled ? "bg-indigo-600" : "bg-gray-200"
                  }`}
                >
                  <span
                    aria-hidden="true"
                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                      editingAction.enabled ? "translate-x-5" : "translate-x-0"
                    }`}
                  />
                </button>
              </div>

              <div className="pt-4 border-t border-gray-100 flex items-center justify-between gap-3">
                <button
                  type="button"
                  onClick={() => handleDeleteAction(editingAction.id)}
                  className="px-3 py-2 text-xs font-semibold text-red-600 hover:bg-red-50 rounded-lg transition-colors cursor-pointer"
                >
                  Delete
                </button>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setEditingAction(null)}
                    className="px-4 py-2 border border-gray-200 rounded-xl text-sm text-gray-600 hover:bg-gray-50 cursor-pointer"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-sm font-semibold cursor-pointer"
                  >
                    Save
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Action Modal */}
      {showAddActionModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 animate-fade-in">
          <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between bg-gray-50/60">
              <h3 className="text-base font-bold text-gray-900">Add Automated Action</h3>
              <button
                onClick={() => setShowAddActionModal(false)}
                className="text-gray-400 hover:text-gray-600 cursor-pointer"
              >
                <i className="ri-close-line text-lg" />
              </button>
            </div>
            <form onSubmit={handleAddNewAction} className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Trigger Condition *</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. When lead reaches Proposal Sent stage"
                  value={newActionForm.trigger}
                  onChange={(e) => setNewActionForm({ ...newActionForm, trigger: e.target.value })}
                  className="w-full px-3.5 py-2 border border-gray-200 rounded-xl text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-500"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Actions to Execute (One per line)
                </label>
                <textarea
                  rows={3}
                  placeholder="e.g. Send SMS notification&#10;Notify team lead"
                  value={newActionForm.description}
                  onChange={(e) => setNewActionForm({ ...newActionForm, description: e.target.value })}
                  className="w-full px-3.5 py-2 border border-gray-200 rounded-xl text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-500 resize-none"
                />
              </div>

              {liveAgents.length > 0 && (
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">
                    Quick Assign to Team Member
                  </label>
                  <select
                    value={newActionForm.targetAgent}
                    onChange={(e) => {
                      const agent = e.target.value;
                      setNewActionForm((prev) => {
                        const base = prev.description ? prev.description + "\n" : "";
                        return { ...prev, targetAgent: agent, description: `${base}Auto-assign to ${agent}` };
                      });
                    }}
                    className="w-full px-3.5 py-2 border border-gray-200 rounded-xl text-sm text-gray-900 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-200"
                  >
                    <option value="">Select an agent...</option>
                    {liveAgents.map((ag) => (
                      <option key={ag} value={ag}>
                        {ag}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className="pt-4 border-t border-gray-100 flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setShowAddActionModal(false)}
                  className="px-4 py-2 border border-gray-200 rounded-xl text-sm text-gray-600 hover:bg-gray-50 cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-sm font-semibold cursor-pointer"
                >
                  Add Action
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </CrmLayout>
  );
}